import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { buildApiUrl } from '../../utils/apiUrl';
import { useEmpresaId } from '../../context/EmpresaContext';
import { supabase } from '../../supabase';
import { enviarReporteWhatsApp } from './enviarReporteWhatsApp';
import { CalendarCheck, RefreshCw, Printer, AlertTriangle, Loader2, MessageCircle, X } from 'lucide-react';

// ── Tipos ────────────────────────────────────────────────────────────────────
interface LaborProgramada {
  id: number;
  labor_nombre: string;
  estadio: string | null;
  grupo_forza: string | null;
  lote_num: string | null;
  fecha_ancla: string | null;
  fecha_programada: string;
  semana: string;
  estado: string;
  clase?: 'muestreo' | 'tarea';
  labor_activa?: boolean;
}
type FiltroClase = 'todos' | 'muestreo' | 'tarea';

// Semana ISO "NN-AA" desde una fecha (igual que el backend).
function semanaLabel(d: Date): string {
  const x = new Date(d.valueOf());
  if (x.getDay() === 0) x.setDate(x.getDate() + 1);
  const target = new Date(x.valueOf());
  const dayNr = (x.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const week = 1 + Math.round(
    ((target.getTime() - firstThursday.getTime()) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7
  );
  return `${String(week).padStart(2, '0')}-${String(target.getFullYear()).slice(-2)}`;
}

const DIAS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
function fmtFecha(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return `${DIAS[d.getDay()]} ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Fecha local de hoy en ISO (yyyy-mm-dd), sin desfase de zona horaria.
function hoyISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function ProgramaMuestreosPage() {
  const empresaId = useEmpresaId();
  // Estándar ERP: el usuario elige la FECHA y la semana se calcula sola (no se escribe a mano).
  const [fecha, setFecha] = useState(() => hoyISO());
  const semana = useMemo(() => (fecha ? semanaLabel(new Date(fecha + 'T00:00:00')) : ''), [fecha]);
  const [programa, setPrograma] = useState<LaborProgramada[]>([]);
  const [filtroClase, setFiltroClase] = useState<FiltroClase>('todos');
  const [loading, setLoading] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [avisos, setAvisos] = useState<string[]>([]);
  const [empresaNombre, setEmpresaNombre] = useState('');
  // Envío por WhatsApp
  const [showWa, setShowWa] = useState(false);
  const [waNumero, setWaNumero] = useState('');
  const [waEnviando, setWaEnviando] = useState(false);

  const enviarWhatsApp = async () => {
    setWaEnviando(true);
    try {
      const r = await enviarReporteWhatsApp({
        html: buildReporteHtml(),
        numero: waNumero,
        caption: `🗓️ *Programa de ${filtroClase === 'tarea' ? 'Tareas' : filtroClase === 'muestreo' ? 'Muestreos' : 'Muestreos y Tareas'}*\nSemana ${semana} · ${programaVisible.length} registro${programaVisible.length === 1 ? '' : 's'}`,
        fileName: `muestreos-${semana}.png`,
        empresaId,
      });
      if (r.ok) {
        setToast({ msg: 'Reporte enviado por WhatsApp.', ok: true });
        setShowWa(false); setWaNumero('');
      } else {
        setToast({ msg: r.error || 'No se pudo enviar.', ok: false });
      }
    } finally {
      setWaEnviando(false);
    }
  };

  useEffect(() => {
    supabase.from('empresas').select('nombre').eq('id', empresaId).maybeSingle()
      .then(({ data }) => setEmpresaNombre(data?.nombre || ''));
  }, [empresaId]);

  const cargar = useCallback(async () => {
    if (!semana) return;
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl(`/aplicaciones/labores/programa?empresa_id=${empresaId}&semana=${encodeURIComponent(semana)}`));
      const j = await res.json();
      setPrograma(j.ok ? (j.programa || []) : []);
    } catch {
      setPrograma([]);
    } finally {
      setLoading(false);
    }
  }, [empresaId, semana]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 5000);
    return () => clearTimeout(t);
  }, [toast]);

  const generar = async () => {
    setGenerando(true);
    setAvisos([]);
    try {
      const res = await fetch(buildApiUrl('/aplicaciones/labores/generar'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ empresa_id: empresaId }),
      });
      const j = await res.json();
      if (!j.ok) { setToast({ msg: j.error || 'Error al generar', ok: false }); return; }
      setAvisos(j.avisos || []);
      setToast({
        msg: `${j.creadas} labores generadas (${j.grupos} grupos de forza)` +
          (j.avisos?.length ? ` — ${j.avisos.length} aviso(s)` : ''),
        ok: !(j.avisos?.length),
      });
      cargar();
    } catch (e: any) {
      setToast({ msg: e?.message || 'Error de red', ok: false });
    } finally {
      setGenerando(false);
    }
  };

  // ── Impresión: HTML dedicado en iframe (NO window.print de la página) ───────
  // Patrón estándar MYA: documento nuevo con CSS embebido y clases propias —
  // no depender de Tailwind (no carga en la ventana hija). Ver feedback_reportes_print.
  // Construye el HTML del reporte (reutilizado por imprimir y por WhatsApp).
  const buildReporteHtml = () => {
    const hoyStr = new Date().toLocaleDateString('es-CR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
    const esc = (s: any) => String(s ?? '').replace(/[<>&]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' }[c] as string));

    const filasHtml = porFecha.map(([fecha, items]) =>
      items.map((p, idx) => `
        <tr>
          <td class="col-fecha">${idx === 0 ? esc(fmtFecha(fecha)) : ''}</td>
          <td>${esc(p.labor_nombre)}</td>
          <td class="mono">${esc(p.estadio || '')}</td>
          <td>${esc(p.lote_num || '')}</td>
          <td class="col-estado">${esc(p.estado)}</td>
        </tr>`).join('')
    ).join('');

    return `<!DOCTYPE html><html lang="es"><head><meta charset="UTF-8">
<title>Programa de ${filtroClase === 'tarea' ? 'Tareas' : filtroClase === 'muestreo' ? 'Muestreos' : 'Muestreos y Tareas'} · Semana ${esc(semana)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; margin: 0; padding: 28px 32px; font-size: 12px; }
  .rep-head { display: table; width: 100%; border-bottom: 2px solid #0ea5e9; padding-bottom: 10px; margin-bottom: 16px; }
  .rep-head .left { display: table-cell; vertical-align: bottom; }
  .rep-head .right { display: table-cell; text-align: right; vertical-align: bottom; color: #64748b; font-size: 10px; }
  .rep-empresa { font-size: 16px; font-weight: 700; color: #0f172a; }
  .rep-title { font-size: 13px; font-weight: 600; color: #0ea5e9; margin-top: 2px; }
  .rep-meta { margin: 0 0 14px; display: table; width: 100%; }
  .rep-meta .box { display: table-cell; padding: 6px 12px; background: #f1f5f9 !important; border: 1px solid #e2e8f0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .rep-meta .box + .box { border-left: none; }
  .rep-meta .lbl { font-size: 8px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; display: block; }
  .rep-meta .val { font-size: 14px; font-weight: 700; color: #0f172a; }
  table.rep { width: 100%; border-collapse: collapse; }
  table.rep thead th { background: #0ea5e9 !important; color: #fff !important; text-align: left; padding: 6px 8px; font-size: 10px; text-transform: uppercase; letter-spacing: 0.05em; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  table.rep tbody td { padding: 5px 8px; border-bottom: 1px solid #e2e8f0; vertical-align: top; }
  table.rep tbody tr:nth-child(even) td { background: #f8fafc !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .col-fecha { font-weight: 600; white-space: nowrap; color: #0f172a; }
  .mono { font-family: 'Consolas', monospace; color: #475569; }
  .col-estado { text-transform: capitalize; color: #475569; }
  .rep-foot { margin-top: 18px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #94a3b8; text-align: center; }
  @page { margin: 14mm; }
</style></head>
<body>
  <div class="rep-head">
    <div class="left">
      <div class="rep-empresa">${esc(empresaNombre || 'Control de Piña')}</div>
      <div class="rep-title">Programa de Campo — ${filtroClase === 'tarea' ? 'Tareas' : filtroClase === 'muestreo' ? 'Muestreos' : 'Muestreos y Tareas'}</div>
    </div>
    <div class="right">Generado: ${esc(hoyStr)}</div>
  </div>
  <div class="rep-meta">
    <div class="box"><span class="lbl">Semana</span><span class="val">${esc(semana)}</span></div>
    <div class="box"><span class="lbl">${filtroClase === 'tarea' ? 'Tareas' : filtroClase === 'muestreo' ? 'Muestreos' : 'Registros'}</span><span class="val">${programaVisible.length}</span></div>
    <div class="box"><span class="lbl">Estadios</span><span class="val">${new Set(programaVisible.map((p) => p.estadio).filter(Boolean)).size}</span></div>
  </div>
  <table class="rep">
    <thead><tr>
      <th style="width:14%">Fecha</th>
      <th style="width:34%">Labor / Muestreo</th>
      <th style="width:14%">Estadio</th>
      <th style="width:26%">Lote(s)</th>
      <th style="width:12%">Estado</th>
    </tr></thead>
    <tbody>${filasHtml}</tbody>
  </table>
  <div class="rep-foot">MYA ERP · Control de Piña — Labores y Muestreos</div>
</body></html>`;
  };

  const imprimirPdf = () => {
    if (programa.length === 0) return;
    const html = buildReporteHtml();
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) { document.body.removeChild(iframe); return; }
    doc.open(); doc.write(html); doc.close();
    setTimeout(() => {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 400);
  };

  // Orden agronómico de estadios (1er ciclo → 2º ciclo) para sub-ordenar.
  const ordenEstadio = (cod: string | null): number => {
    const ORD: Record<string, number> = { PRE: 0, BAR: 1, SPC: 2, DPC: 3, FPC: 4, SRC: 5, DRC: 6, FRC: 7 };
    return cod && cod in ORD ? ORD[cod] : 99;
  };

  // Excluir inactivos (labor del catálogo desactivada) y filtrar por clase.
  const programaVisible = useMemo(() =>
    programa
      .filter((p) => p.labor_activa !== false)
      .filter((p) => filtroClase === 'todos' || (p.clase || 'muestreo') === filtroClase),
    [programa, filtroClase]
  );

  // Agrupar por fecha y, dentro de cada fecha, sub-ordenar por ESTADIO y labor.
  const porFecha = useMemo(() => {
    const m = new Map<string, LaborProgramada[]>();
    for (const p of programaVisible) {
      const arr = m.get(p.fecha_programada) || [];
      arr.push(p);
      m.set(p.fecha_programada, arr);
    }
    for (const arr of m.values()) {
      arr.sort((a, b) =>
        ordenEstadio(a.estadio) - ordenEstadio(b.estadio) ||
        (a.estadio || '').localeCompare(b.estadio || '') ||
        (a.labor_nombre || '').localeCompare(b.labor_nombre || '')
      );
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [programaVisible]);

  const labelCls = 'text-[9px] uppercase tracking-widest mb-0.5';

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: 'var(--surface)' }}>
      {/* Encabezado */}
      <div className="px-5 py-3 shrink-0 flex items-center gap-4 flex-wrap"
        style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="flex items-center gap-2">
          <CalendarCheck size={20} style={{ color: 'var(--emp-accent-txt)' }} />
          <div>
            <h1 className="text-base font-bold" style={{ color: 'var(--ink)' }}>
              Programa de {filtroClase === 'tarea' ? 'Tareas' : filtroClase === 'muestreo' ? 'Muestreos' : 'Muestreos y Tareas'}
            </h1>
            <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>Labores y muestreos de campo — por estadio</p>
          </div>
        </div>

        <div className="ml-auto flex items-end gap-4">
          {/* Estándar ERP: elegir fecha → la semana se calcula sola (no se escribe a mano) */}
          <div>
            <p className={labelCls} style={{ color: 'var(--ink-faint)' }}>Fecha</p>
            <input
              type="date"
              className="text-sm bg-transparent outline-none"
              style={{ color: 'var(--ink)', borderBottom: '1px solid var(--line)', padding: '2px 0', colorScheme: 'dark' }}
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>
          <div className="flex flex-col items-center">
            <span className="text-[9px] uppercase tracking-widest" style={{ color: 'var(--ink-faint)' }}>Semana</span>
            <span className="text-base font-bold font-mono" style={{ color: 'var(--emp-accent-txt)' }}>{semana || '—'}</span>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-faint)' }}>Clase</p>
            <select value={filtroClase} onChange={(e) => setFiltroClase(e.target.value as FiltroClase)}
              className="text-sm bg-transparent outline-none rounded px-2 py-1.5"
              style={{ color: 'var(--ink)', border: '1px solid var(--line)', backgroundColor: 'var(--surface-raised)', colorScheme: 'light dark' }}>
              <option value="todos" style={{ background: 'var(--surface-raised)', color: 'var(--ink)' }}>Todos</option>
              <option value="muestreo" style={{ background: 'var(--surface-raised)', color: 'var(--ink)' }}>Muestreos</option>
              <option value="tarea" style={{ background: 'var(--surface-raised)', color: 'var(--ink)' }}>Tareas</option>
            </select>
          </div>
          <button onClick={cargar} disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
            {loading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Consultar
          </button>
          <button onClick={generar} disabled={generando}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-semibold"
            style={{ background: 'var(--emp-accent)', color: 'var(--emp-accent-on)', opacity: generando ? 0.6 : 1 }}>
            {generando ? <Loader2 size={13} className="animate-spin" /> : <CalendarCheck size={13} />}
            Generar calendario
          </button>
          <button onClick={imprimirPdf} disabled={programa.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)', opacity: programa.length === 0 ? 0.5 : 1 }}>
            <Printer size={13} /> Imprimir
          </button>
          <button onClick={() => setShowWa(true)} disabled={programa.length === 0}
            className="flex items-center gap-1.5 px-3 py-2 rounded text-xs font-medium"
            style={{ background: 'var(--surface-deep)', border: '1px solid var(--line)', color: 'var(--ink-muted)', opacity: programa.length === 0 ? 0.5 : 1 }}>
            <MessageCircle size={13} /> WhatsApp
          </button>
        </div>
      </div>

      {/* Modal: enviar reporte por WhatsApp (imagen) */}
      {showWa && createPortal((
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
          style={{ background: 'rgba(8,12,22,0.72)' }} onClick={() => !waEnviando && setShowWa(false)}>
          <div className="rounded-xl w-full max-w-sm overflow-hidden"
            style={{ background: 'var(--surface-base)', border: '1px solid var(--emp-accent)', boxShadow: '0 24px 60px rgba(0,0,0,0.55)' }}
            onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid var(--line)' }}>
              <MessageCircle size={18} style={{ color: '#22c55e' }} />
              <h2 className="text-sm font-bold flex-1" style={{ color: 'var(--ink)' }}>Enviar por WhatsApp</h2>
              <button onClick={() => !waEnviando && setShowWa(false)} className="p-1 rounded" style={{ color: 'var(--ink-faint)' }}><X size={18} /></button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-[11px]" style={{ color: 'var(--ink-faint)' }}>
                Se enviará el reporte de la semana <b style={{ color: 'var(--ink-muted)' }}>{semana}</b> como imagen.
              </p>
              <div>
                <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: 'var(--ink-faint)' }}>Número de WhatsApp</p>
                <input type="tel" inputMode="tel" autoFocus
                  value={waNumero} onChange={(e) => setWaNumero(e.target.value)}
                  placeholder="Ej. 50688887777"
                  className="w-full text-sm rounded px-3 py-2 bg-transparent outline-none"
                  style={{ color: 'var(--ink)', border: '1px solid var(--line)' }} />
                <p className="text-[10px] mt-1" style={{ color: 'var(--ink-faint)' }}>Con código de país (Costa Rica: 506).</p>
              </div>
            </div>
            <div className="px-5 py-3 flex items-center justify-end gap-2" style={{ borderTop: '1px solid var(--line)' }}>
              <button onClick={() => setShowWa(false)} disabled={waEnviando}
                className="px-3 py-2 rounded text-xs font-medium" style={{ border: '1px solid var(--line)', color: 'var(--ink-muted)' }}>Cancelar</button>
              <button onClick={enviarWhatsApp} disabled={waEnviando || waNumero.replace(/\D/g, '').length < 8}
                className="flex items-center gap-1.5 px-4 py-2 rounded text-xs font-semibold"
                style={{ background: '#22c55e', color: '#fff', opacity: (waEnviando || waNumero.replace(/\D/g, '').length < 8) ? 0.6 : 1 }}>
                {waEnviando ? <Loader2 size={13} className="animate-spin" /> : <MessageCircle size={13} />} Enviar
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {/* Avisos de generación (grupos con fechas múltiples) */}
      {avisos.length > 0 && (
        <div className="px-5 py-2 shrink-0 text-xs" style={{ background: '#fff4e5', borderBottom: '1px solid #ffb74d', color: '#a15c00' }}>
          <div className="flex items-center gap-2 font-semibold"><AlertTriangle size={14} /> Avisos de generación:</div>
          <ul className="mt-1 list-disc pl-6">
            {avisos.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="px-5 py-2 shrink-0 text-xs font-medium"
          style={{ background: toast.ok ? 'var(--tone-success-bg)' : '#fff4e5', color: toast.ok ? 'var(--tone-success-text)' : '#a15c00', borderBottom: `1px solid ${toast.ok ? 'var(--tone-success-border)' : '#ffb74d'}` }}>
          {toast.msg}
        </div>
      )}

      {/* Tabla del programa */}
      <div className="flex-1 overflow-auto p-5">
        <div className="mb-2 text-xs" style={{ color: 'var(--ink-muted)' }}>
          Programa de muestreos — semana <b>{semana}</b> · {programaVisible.length} muestreo{programaVisible.length !== 1 ? 's' : ''}
        </div>
        {programaVisible.length === 0 ? (
          <div className="py-12 text-center text-sm" style={{ color: 'var(--ink-faint)' }}>
            {loading ? 'Cargando…' : 'Sin muestreos para este filtro/semana. Probá "Generar calendario" o cambiá el filtro.'}
          </div>
        ) : (
          <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--line)', color: 'var(--ink-muted)' }}>
                <th className="text-left py-1.5 px-2 text-[11px] uppercase tracking-wider">Fecha</th>
                <th className="text-left py-1.5 px-2 text-[11px] uppercase tracking-wider">Labor / Muestreo</th>
                <th className="text-left py-1.5 px-2 text-[11px] uppercase tracking-wider">Estadio</th>
                <th className="text-left py-1.5 px-2 text-[11px] uppercase tracking-wider">Lote(s)</th>
                <th className="text-left py-1.5 px-2 text-[11px] uppercase tracking-wider">Estado</th>
              </tr>
            </thead>
            <tbody>
              {porFecha.map(([fecha, items]) =>
                items.map((p, idx) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid var(--line-dim)' }}>
                    <td className="py-1.5 px-2 font-mono" style={{ color: 'var(--ink)' }}>
                      {idx === 0 ? fmtFecha(fecha) : ''}
                    </td>
                    <td className="py-1.5 px-2" style={{ color: 'var(--ink)' }}>{p.labor_nombre}</td>
                    <td className="py-1.5 px-2 font-mono" style={{ color: 'var(--ink-muted)' }}>{p.estadio}</td>
                    <td className="py-1.5 px-2" style={{ color: 'var(--ink-muted)' }}>{p.lote_num}</td>
                    <td className="py-1.5 px-2">
                      <span className="text-[10px] px-1.5 py-0.5 rounded"
                        style={{ background: 'var(--tone-info-bg)', color: 'var(--tone-info-text)' }}>
                        {p.estado}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
